import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNavBar from '../dashboard/components/TopNavBar';
import Icon from '../../components/AppIcon';
import GenericMasterDataTab from './components/GenericMasterDataTab';
import ImagingMasterTab from './components/ImagingMasterTab';
import FinanceBillingTab from './components/FinanceBillingTab';
import NavigationPreventer from '../../components/NavigationPreventer';
import { useAuth } from '../../state/auth/useAuth';
import { getMasterDataOptions } from '../../api/masterData';

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [navTab, setNavTab] = useState('master-data');
  const [activeTab, setActiveTab] = useState('medicines');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleNavTabChange = (tabId) => {
    setNavTab(tabId);
    switch(tabId) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'master-data':
        // Stay on master data page
        break;
      case 'patients':
        navigate('/users');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'reports':
        navigate('/accounting');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'settings':
        navigate('/hospital-settings');
        break;
      case 'profile':
        console.log('Profile navigation - route not yet configured');
        break;
      default:
        break;
    }
  };

  // Handle URL parameters to set active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const masterDataOptions = getMasterDataOptions();

  const getCurrentMasterDataLabel = () => {
    const option = masterDataOptions.find(opt => opt.id === activeTab);
    return option ? option.label : 'Master Data';
  };

  const renderTabContent = () => {
    console.log('Current active tab:', activeTab); // Debug log

    // Finance & Billing tab
    if (activeTab === 'finance-billing') {
      return <FinanceBillingTab />;
    }
    
    // Check if it's imaging-master tab (custom component)
    if (activeTab === 'imaging-master') {
      return <ImagingMasterTab />;
    }
    
    // Check if it's a master data tab
    const masterDataOption = masterDataOptions.find(opt => opt.id === activeTab);
    if (masterDataOption) {
      return <GenericMasterDataTab dataType={activeTab} />;
    }
    
    // Default to first master data option if no valid tab
    return <GenericMasterDataTab dataType={masterDataOptions[0]?.id || 'medicines'} />;
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Poppins' }}>
      <NavigationPreventer />
      <TopNavBar 
        user={user}
        activeTab={navTab}
        onTabChange={handleNavTabChange}
      />
      <main className="pt-20 px-4 lg:px-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-5">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Master Data Management
            </h1>
            <p className="text-sm text-gray-600">Manage master data and administrative settings</p>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {/* Master Data Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`${
                      masterDataOptions.some(opt => opt.id === activeTab)
                        ? 'border-[#2D869C] text-[#2D869C]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200`}
                  >
                    <Icon name="Database" size={16} />
                    <span>{getCurrentMasterDataLabel()}</span>
                    <Icon 
                      name={isDropdownOpen ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      className="ml-1" 
                    />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-64 rounded-xl shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50 border-2 border-gray-100">
                      <div className="py-2 max-h-96 overflow-y-auto" role="menu">
                        {masterDataOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => {
                              setActiveTab(option.id);
                              setIsDropdownOpen(false);
                            }}
                            className={`${
                              activeTab === option.id
                                ? 'bg-[#2D869C]/10 text-[#2D869C] font-bold border-l-4 border-[#2D869C]'
                                : 'text-gray-700 hover:bg-gray-50 font-semibold border-l-4 border-transparent'
                            } w-full text-left px-4 py-3 text-base transition-all duration-200 flex items-center space-x-3 hover:translate-x-1`}
                            role="menuitem"
                          >
                            <Icon name="Database" size={16} />
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </nav>
            </div>
            
            {/* Tab Content */}
            <div className="p-5">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
